"use client";

import { useMemo } from "react";
import styled from "styled-components";
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { formatMoney } from "@/utils/format";
import { Info } from "lucide-react";
import type { ExpenseBreakdown, OzonDashboardSummary, DashboardSummary } from "./types";

interface ExpenseCardsProps {
  marketplace: "wildberries" | "ozon";
  wbSummary?: DashboardSummary;
  ozonSummary?: OzonDashboardSummary;
  isLoading: boolean;
  storeName?: string;
}

interface ExpenseItem {
  key: string;
  label: string;
  color: string;
  tooltip?: string;
  value: number;
  percentage: number;
}

const WB_EXPENSE_CONFIG = [
  { key: "logistics", label: "Логистика", color: "var(--color-expense-logistics)", tooltip: "Стоимость доставки товаров" },
  { key: "cost_of_goods", label: "Себестоимость", color: "var(--color-expense-cogs)", tooltip: "Себестоимость проданных товаров (по методу FIFO)" },
  { key: "storage", label: "Хранение", color: "var(--color-expense-storage)", tooltip: "Плата за хранение на складе WB" },
  { key: "ad_cost", label: "Реклама", color: "var(--color-expense-ads)", tooltip: "Затраты на рекламные кампании" },
  { key: "penalties", label: "Штрафы", color: "var(--color-expense-penalties)", tooltip: "Штрафы и удержания" },
];

const OZON_EXPENSE_CONFIG = [
  { key: "processing_and_delivery", label: "Логистика", color: "var(--color-expense-logistics)", tooltip: "Доставка и возвраты" },
  { key: "cost_of_goods", label: "Себестоимость", color: "var(--color-expense-cogs)", tooltip: "Себестоимость проданных товаров" },
  { key: "services_amount", label: "Хранение", color: "var(--color-expense-storage)", tooltip: "Хранение на складах Ozon" },
  { key: "advertising_cost", label: "Реклама", color: "var(--color-expense-ads)", tooltip: "Затраты на рекламные кампании" },
  { key: "other_expenses", label: "Прочие расходы", color: "var(--color-expense-other)", tooltip: "Кроссдокинг, FBO, эквайринг, упаковка и др." },
  { key: "refunds_and_cancellations", label: "Возвраты", color: "var(--color-expense-penalties)", tooltip: "Возвраты и отмены" },
];

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const TitleWithIcon = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 500;
  color: var(--color-text-secondary);
`;

const TotalAmount = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary);
`;

const ExpenseList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ExpenseRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ExpenseHeader = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 14px;
`;

const ExpenseLabel = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  color: var(--color-text-secondary);
`;

const InfoIcon = styled(Info)`
  width: 14px;
  height: 14px;
  color: var(--color-text-tertiary);
  cursor: help;
`;

const ExpenseValues = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Percentage = styled.span`
  font-size: 12px;
  color: var(--color-text-tertiary);
`;

const Amount = styled.span`
  font-weight: 500;
  color: var(--color-text-primary);
`;

const ProgressBar = styled.div`
  height: 8px;
  background-color: var(--color-bg-muted);
  border-radius: 9999px;
  overflow: hidden;
`;

const ProgressFill = styled.div<{ $color: string; $width: number }>`
  height: 100%;
  border-radius: 9999px;
  background-color: ${({ $color }) => $color};
  width: ${({ $width }) => $width}%;
  transition: width 0.3s ease;
`;

const FullHeightCard = styled.div``;

const EmptyState = styled.div`
  text-align: center;
  padding: 16px 0;
  font-size: 14px;
  color: var(--color-text-tertiary);
`;

const LoadingStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const LoadingItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

function processWbExpenses(summary: DashboardSummary | undefined): { expenses: ExpenseItem[]; total: number } {
  if (!summary?.expenses) {
    return { expenses: [], total: 0 };
  }

  const exp = summary.expenses;
  const costOfGoods = Math.abs(parseFloat(summary.cost_of_goods || "0"));
  const expensesTotal = parseFloat(exp.total || "0");
  const total = expensesTotal + costOfGoods;

  const expenses = WB_EXPENSE_CONFIG.map((config) => {
    const value =
      config.key === "cost_of_goods"
        ? costOfGoods
        : Math.abs(parseFloat(exp[config.key as keyof ExpenseBreakdown] || "0"));
    return {
      ...config,
      value,
      percentage: total > 0 ? (value / total) * 100 : 0,
    };
  })
    .filter((item) => item.value > 0)
    .sort((a, b) => b.value - a.value);

  return { expenses, total };
}

function processOzonExpenses(summary: OzonDashboardSummary | undefined): { expenses: ExpenseItem[]; total: number } {
  if (!summary) {
    return { expenses: [], total: 0 };
  }

  const costOfGoods = Math.abs(parseFloat(summary.cost_of_goods || "0"));
  const expensesTotal = Math.abs(parseFloat(summary.total_expenses || "0"));
  const total = expensesTotal + costOfGoods;

  const expenses = OZON_EXPENSE_CONFIG.map((config) => {
    const value =
      config.key === "cost_of_goods"
        ? costOfGoods
        : Math.abs(parseFloat((summary[config.key as keyof OzonDashboardSummary] as string) || "0"));
    return {
      ...config,
      value,
      percentage: total > 0 ? (value / total) * 100 : 0,
    };
  })
    .filter((item) => item.value > 0)
    .sort((a, b) => b.value - a.value);

  return { expenses, total };
}

export function ExpenseCards({ marketplace, wbSummary, ozonSummary, isLoading, storeName }: ExpenseCardsProps) {
  const isWb = marketplace === "wildberries";
  const defaultTitle = isWb ? "Расходы WB" : "Расходы Ozon";
  const title = storeName ? `Расходы · ${storeName}` : defaultTitle;

  const expenseData = useMemo(() => {
    if (isWb) {
      return processWbExpenses(wbSummary);
    }
    return processOzonExpenses(ozonSummary);
  }, [isWb, wbSummary, ozonSummary]);

  if (isLoading) {
    return (
      <FullHeightCard>
        <CardHeader>
          <TitleWithIcon>
            {title}
          </TitleWithIcon>
        </CardHeader>
        <CardContent>
          <LoadingStack>
            {[1, 2, 3].map((i) => (
              <LoadingItem key={i}>
                <Skeleton width={96} height={16} />
                <Skeleton width="100%" height={8} />
              </LoadingItem>
            ))}
          </LoadingStack>
        </CardContent>
      </FullHeightCard>
    );
  }

  return (
    <FullHeightCard>
      <CardHeader>
        <HeaderRow>
          <TitleWithIcon>
            {title}
          </TitleWithIcon>
          <TotalAmount>{formatMoney(expenseData.total)}</TotalAmount>
        </HeaderRow>
      </CardHeader>
      <CardContent>
        {expenseData.expenses.length === 0 ? (
          <EmptyState>Нет данных за период</EmptyState>
        ) : (
          <TooltipProvider>
            <ExpenseList>
              {expenseData.expenses.map((expense) => (
                <ExpenseRow key={expense.key}>
                  <ExpenseHeader>
                    <ExpenseLabel>
                      {expense.label}
                      {expense.tooltip && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <InfoIcon />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{expense.tooltip}</p>
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </ExpenseLabel>
                    <ExpenseValues>
                      <Percentage>{expense.percentage.toFixed(1)}%</Percentage>
                      <Amount>{formatMoney(expense.value)}</Amount>
                    </ExpenseValues>
                  </ExpenseHeader>
                  <ProgressBar>
                    <ProgressFill $color={expense.color} $width={expense.percentage} />
                  </ProgressBar>
                </ExpenseRow>
              ))}
            </ExpenseList>
          </TooltipProvider>
        )}
      </CardContent>
    </FullHeightCard>
  );
}
